import React, { useState, useEffect } from 'react';
import './QuickStats.css';
import QuickStatsSettings from './QuickStatsSettings';
import settingsIcon from '../../../assets/settings.svg';

function QuickStats({ stats, user, onPrefsUpdate }) {
  const [showSettings, setShowSettings] = useState(false);
  const [animatedValues, setAnimatedValues] = useState({});

  // Max values for progress calculation
  const maxValues = {
    total_contacts: 100,
    added_this_week: 20,
    tasks_completed_today: 10,
    pending_tasks: 20,
    total_conversations_started: 50,
    total_presentations_finished: 30,
    total_followups: 50,
    total_closings: 20,
    total_customers: 50,
    total_marketers: 20
  };

  // Stat labels mapping
  const statLabels = {
    total_contacts: 'Total Contacts: ',
    added_this_week: 'Added This Week: ',
    tasks_completed_today: 'Tasks Completed Today: ',
    pending_tasks: 'Pending Tasks: ',
    total_conversations_started: 'Conversations Started: ',
    total_presentations_finished: 'Presentations Finished: ',
    total_followups: 'Follow-ups: ',
    total_closings: 'Closings: ',
    total_customers: 'Total Customers: ',
    total_marketers: 'Total Marketers: '
  };

  // Gradient colors (cycle through)
  const gradients = ['blue', 'green', 'purple', 'orange'];

  // Get user preferences or default
  const userPrefs = user?.quick_stats_prefs || ['total_contacts', 'added_this_week', 'tasks_completed_today', 'pending_tasks'];

  // Count-up animation
  useEffect(() => {
    if (!stats) return;

    const duration = 1000;
    const steps = 60;
    const interval = duration / steps;
    
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      const newValues = {};
      Object.keys(stats).forEach(key => {
        newValues[key] = Math.floor((stats[key] || 0) * progress);
      });
      
      setAnimatedValues(newValues);
      
      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedValues(stats);
      }
    }, interval);
    
    return () => clearInterval(timer);
  }, [stats]);

  // Build stat items based on user preferences
  const statItems = userPrefs.map((key, index) => {
    // ALL bars are orange EXCEPT "Tasks Completed Today" which is green
    let gradient = 'orange';  // Default to orange for ALL
    if (key === 'tasks_completed_today') {
      gradient = 'green';  // Only Tasks Completed Today is green
    }
    
    return {
      key,
      label: statLabels[key] || key,
      value: animatedValues[key] || 0,
      actualValue: stats?.[key] || 0,
      maxValue: maxValues[key] || 100,
      gradient: gradient
    };
  });

  const handleSavePrefs = (newPrefs) => {
    if (onPrefsUpdate) {
      onPrefsUpdate(newPrefs);
    }
  };

  return (
    <>
      <div className="quick-stats-card">
        <div className="quick-stats-header">
          <h3 className="quick-stats-title">Quick Stats</h3>
          <img 
            src={settingsIcon} 
            alt="Settings" 
            className="settings-icon"
            onClick={() => setShowSettings(true)}
          />
        </div>
        
        {statItems.map((item) => {
          const progress = Math.min((item.actualValue / item.maxValue) * 100, 100);
          
          return (
            <div key={item.key} className="quick-stat-item">
              <div className="stat-line">
                <span className="quick-stat-label">{item.label}</span>
                <span className="quick-stat-number">{item.value}</span>
              </div>
              <div className="quick-stats-progress-container">
                <div 
                  className={`quick-stats-progress-fill gradient-${item.gradient}`}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      {showSettings && (
        <QuickStatsSettings
          onClose={() => setShowSettings(false)}
          currentPrefs={userPrefs}
          onSave={handleSavePrefs}
        />
      )}
    </>
  );
}

export default QuickStats;
